import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { LoadingSpinner } from './loading-spinner';
import { ButtonHTMLAttributes, ComponentProps, ElementType, forwardRef, Fragment } from 'react';

export const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer',
  {
    variants: {
      variant: {
        default: 'bg-gray-900 text-white hover:bg-gray-800',
        success: 'bg-green-500 text-white hover:bg-green-600',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        'success-outline': 'border border-green-500 text-green-700 hover:bg-green-50',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  loadingColor?: string;
  component?: ElementType | keyof JSX.IntrinsicElements;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading,
      loadingColor = '#fff',
      children,
      disabled,
      component,
      type = 'button',
      icon,
      ...props
    },
    ref,
  ) => {
    const Comp = component ? component : asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={type}
        disabled={disabled || loading}
        {...props}
      >
        <Fragment>
          {loading ? <LoadingSpinner className="mr-2" color={loadingColor} size="sm" /> : icon}{' '}
          {children}
        </Fragment>
      </Comp>
    );
  },
);
Button.displayName = 'Button';

interface LinkButtonProps extends ComponentProps<'a'>, VariantProps<typeof buttonVariants> {
  component?: ElementType | keyof JSX.IntrinsicElements;
}

export const LinkButton = ({
  className,
  variant,
  size,
  component: Component = 'a',
  ...props
}: LinkButtonProps) => {
  return <Component className={cn(buttonVariants({ variant, size, className }))} {...props} />;
};
LinkButton.displayName = 'LinkButton';
